import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import {
  useSupportReportStore,
  ReportFilters,
  getInitialSystemDateRange,
  formatDisplayDateTime,
  SlaQueueItem,
} from '../../../store/supportReportStore';
import { useSupportSettingsStore } from '../../../store/supportSettingsStore';
import { BarChart3, FileSpreadsheet, Download, TrendingUp, Clock, CheckCircle2, Users, Star, ShieldCheck, Ticket as TicketIcon, Filter, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Copy, ClipboardPaste, ZoomOut, FileCode, Send, X, RefreshCw, Bot, Calendar, Search, Check, Trash2, Play, RotateCcw, ShieldAlert, Table as TableIcon } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { cn } from '../../../lib/utils';

// Color tokens for charts
const DONUT_COLORS = [
  '#2563EB', // Blue / Primary
  '#0284C7', // Sky / Info
  '#16A34A', // Green / Success
  '#D97706', // Amber / Warning
  '#8B5CF6', // Purple
  '#475569', // Slate neutral
];

const PRIORITY_COLORS: Record<string, string> = {
  'Urgent / Critical': '#DC2626',
  Urgent: '#DC2626',
  Critical: '#DC2626',
  High: '#D97706',
  Medium: '#2563EB',
  Low: '#16A34A',
};

export function SupportReports() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab State
  const initialTab = (searchParams.get('tab') as any) || 'ticket';
  const [activeTab, setActiveTab] = useState<'ticket' | 'agent' | 'csat' | 'sla'>(
    ['ticket', 'agent', 'csat', 'sla'].includes(initialTab) ? initialTab : 'ticket'
  );

  // Store state
  const {
    filters,
    setFilter,
    setFilters,
    resetFilters,
    autoRefreshInterval,
    setAutoRefreshInterval,
    ticketReport,
    agentReport,
    csatReport,
    slaReport,
    schedules,
    isLoading,
    isExporting,
    fetchActiveTabReport,
    exportReportFile,
    dispatchSummaryCard,
    fetchSchedules,
    deleteSchedule,
    sendScheduleNow,
  } = useSupportReportStore();

  const { agents: storeAgents, queues: storeQueues, fetchAgents, fetchQueues } = useSupportSettingsStore();

  // Summary Card Modal State
  const [showSummaryCardModal, setShowSummaryCardModal] = useState(false);
  const [summaryType, setSummaryType] = useState<'daily' | 'weekly' | 'monthly' | 'custom'>('daily');
  const [summaryChannel, setSummaryChannel] = useState<'gchat_webhook' | 'email' | 'both'>('email');
  const [summaryEmail, setSummaryEmail] = useState('admin@trustybytes.com');
  const [summaryWebhookUrl, setSummaryWebhookUrl] = useState('');
  const [summaryCustomTitle, setSummaryCustomTitle] = useState('IT Support Operations Summary Card');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleCron, setScheduleCron] = useState('0 9 * * 1');
  const [isDispatchingSummary, setIsDispatchingSummary] = useState(false);

  // Multi-select Dropdown Open States
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Export Dropdown State
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Time Range Popover State (Grafana Style matching Image 2)
  const [showTimePicker, setShowTimePicker] = useState(false);
  const initialSystemRange = useMemo(() => getInitialSystemDateRange(), []);
  const systemTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local';
    } catch {
      return 'Local';
    }
  }, []);

  const [userHasChangedDate, setUserHasChangedDate] = useState(() => searchParams.get('user_date') === '1');
  const [fromInput, setFromInput] = useState(() => {
    const urlFrom = searchParams.get('from');
    const isUserDate = searchParams.get('user_date') === '1';
    if (isUserDate && urlFrom) return formatDisplayDateTime(urlFrom, false);
    return filters.from || initialSystemRange.from;
  });
  const [toInput, setToInput] = useState(() => {
    const urlTo = searchParams.get('to');
    const isUserDate = searchParams.get('user_date') === '1';
    if (isUserDate && urlTo) return formatDisplayDateTime(urlTo, true);
    return filters.to || initialSystemRange.to;
  });
  const [quickSearchQuery, setQuickSearchQuery] = useState('');
  const [recentRanges, setRecentRanges] = useState<string[]>(() => {
    const init = getInitialSystemDateRange();
    return [`${init.from} to ${init.to}`];
  });
  const [queueViewMode, setQueueViewMode] = useState<'chart' | 'table'>('chart');

  // Helper to parse "YYYY-MM-DD HH:mm:ss" in local time safely across all browsers
  const parseLocalDate = (s: string, isEnd = false): Date => {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2})[:.](\d{2})(?:[:.](\d{2}))?)?$/.exec(s.trim());
    if (!m) {
      const d = new Date(s);
      return isNaN(d.getTime()) ? new Date() : d;
    }
    const y = +m[1];
    const mo = +m[2] - 1;
    const d = +m[3];
    const h = m[4] !== undefined ? +m[4] : (isEnd ? 23 : 0);
    const mi = m[5] !== undefined ? +m[5] : (isEnd ? 59 : 0);
    const sec = m[6] !== undefined ? +m[6] : (isEnd ? 59 : 0);
    return new Date(y, mo, d, h, mi, sec);
  };

  const QUICK_RANGES = [
    { label: 'Last 5 minutes', durationMin: 5 },
    { label: 'Last 15 minutes', durationMin: 15 },
    { label: 'Last 30 minutes', durationMin: 30 },
    { label: 'Last 1 hour', durationMin: 60 },
    { label: 'Last 3 hours', durationMin: 180 },
    { label: 'Last 6 hours', durationMin: 360 },
    { label: 'Last 12 hours', durationMin: 720 },
    { label: 'Last 24 hours', durationMin: 1440, preset: 'today' },
    { label: 'Last 2 days', durationDays: 2 },
    { label: 'Last 7 days', durationDays: 7, preset: '7d' },
    { label: 'Last 30 days', durationDays: 30, preset: '30d' },
    { label: 'Last 90 days', durationDays: 90 },
    { label: 'This month', preset: 'this_month' },
    { label: 'Last month', preset: 'last_month' },
  ];

  const handleApplyQuickRange = (range: (typeof QUICK_RANGES)[0]) => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

    let fromDate = new Date();
    if (range.durationMin) {
      fromDate = new Date(now.getTime() - range.durationMin * 60 * 1000);
    } else if (range.durationDays) {
      fromDate = new Date(now.getTime() - range.durationDays * 24 * 60 * 60 * 1000);
    } else if (range.preset === 'today') {
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    } else if (range.preset === 'this_month') {
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
    } else if (range.preset === 'last_month') {
      fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
    } else {
      fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    const fromStr = fmt(fromDate);
    const toStr = fmt(now);
    setFromInput(fromStr);
    setToInput(toStr);
    setUserHasChangedDate(true);
    setFilters({ date_range: 'custom', from: fromStr, to: toStr });
    setShowTimePicker(false);
  };

  const handleApplyAbsoluteRange = () => {
    if (!fromInput || !toInput) {
      toast.error('Please enter both From and To timestamps');
      return;
    }
    const from = formatDisplayDateTime(fromInput, false);
    const to = formatDisplayDateTime(toInput, true);
    if (!from || !to) {
      toast.error('Use format YYYY-MM-DD HH:mm:ss (24-hour)');
      return;
    }
    if (from > to) {
      toast.error('From must be before To');
      return;
    }
    setFromInput(from);
    setToInput(to);
    setUserHasChangedDate(true);
    const rangeStr = `${from} to ${to}`;
    setRecentRanges((prev) => [rangeStr, ...prev.filter((r) => r !== rangeStr)].slice(0, 5));
    setFilters({ date_range: 'custom', from, to });
    setShowTimePicker(false);
    toast.success('Applied custom time range');
  };

  const handleShiftTimeWindow = (direction: 'back' | 'forward') => {
    const fromD = parseLocalDate(fromInput, false);
    const toD = parseLocalDate(toInput, true);
    if (isNaN(fromD.getTime()) || isNaN(toD.getTime())) return;
    const diff = toD.getTime() - fromD.getTime();
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

    const newFrom = new Date(direction === 'back' ? fromD.getTime() - diff : fromD.getTime() + diff);
    const newTo = new Date(direction === 'back' ? toD.getTime() - diff : toD.getTime() + diff);
    const fromStr = fmt(newFrom);
    const toStr = fmt(newTo);
    setFromInput(fromStr);
    setToInput(toStr);
    setUserHasChangedDate(true);
    setFilters({ date_range: 'custom', from: fromStr, to: toStr });
  };

  const handleZoomOut = () => {
    const fromD = parseLocalDate(fromInput, false);
    const toD = parseLocalDate(toInput, true);
    if (isNaN(fromD.getTime()) || isNaN(toD.getTime())) return;
    const diff = toD.getTime() - fromD.getTime();
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

    const newFrom = new Date(fromD.getTime() - diff / 2);
    const newTo = new Date(toD.getTime() + diff / 2);
    const fromStr = fmt(newFrom);
    const toStr = fmt(newTo);
    setFromInput(fromStr);
    setToInput(toStr);
    setUserHasChangedDate(true);
    setFilters({ date_range: 'custom', from: fromStr, to: toStr });
  };

  // Load support agents and queues on mount
  useEffect(() => {
    fetchAgents();
    fetchQueues();
    fetchSchedules();
  }, [fetchAgents, fetchQueues, fetchSchedules]);

  // Initial Sync from URL parameters to Store Filters
  // Defaults to current system date (00:00:00 to 23:59:59) until user explicitly changes it
  useEffect(() => {
    const urlFrom = searchParams.get('from');
    const urlTo = searchParams.get('to');
    const userModified = searchParams.get('user_date') === '1';
    const urlGroupBy = searchParams.get('group_by');
    const urlStatus = searchParams.get('status');
    const urlPriority = searchParams.get('priority');
    const urlType = searchParams.get('ticket_type');
    const urlSource = searchParams.get('source');

    const init = getInitialSystemDateRange();
    const updates: Partial<ReportFilters> = {};
    if (urlGroupBy) updates.group_by = urlGroupBy as any;
    if (urlStatus) updates.status = urlStatus.split(',').filter(Boolean);
    if (urlPriority) updates.priority = urlPriority.split(',').filter(Boolean);
    if (urlType) updates.ticket_type = urlType.split(',').filter(Boolean);
    if (urlSource) updates.source = urlSource.split(',').filter(Boolean);

    if (userModified && urlFrom && urlTo) {
      const formattedFrom = formatDisplayDateTime(urlFrom, false);
      const formattedTo = formatDisplayDateTime(urlTo, true);
      updates.from = formattedFrom;
      updates.to = formattedTo;
      updates.date_range = 'custom';
      setFromInput(formattedFrom);
      setToInput(formattedTo);
      setUserHasChangedDate(true);
    } else {
      updates.from = init.from;
      updates.to = init.to;
      updates.date_range = 'custom';
      setFromInput(init.from);
      setToInput(init.to);
      setUserHasChangedDate(false);
    }

    setFilters(updates);
  }, []);

  // Update URL search parameters whenever filters or activeTab change
  useEffect(() => {
    const newParams = new URLSearchParams();
    newParams.set('tab', activeTab);
    if (filters.date_range) newParams.set('date_range', filters.date_range);
    if (filters.from) newParams.set('from', filters.from);
    if (filters.to) newParams.set('to', filters.to);
    if (userHasChangedDate) newParams.set('user_date', '1');
    if (filters.group_by) newParams.set('group_by', filters.group_by);
    if (filters.status && filters.status.length > 0) newParams.set('status', filters.status.join(','));
    if (filters.priority && filters.priority.length > 0) newParams.set('priority', filters.priority.join(','));
    if (filters.ticket_type && filters.ticket_type.length > 0) newParams.set('ticket_type', filters.ticket_type.join(','));
    if (filters.source && filters.source.length > 0) newParams.set('source', filters.source.join(','));

    setSearchParams(newParams, { replace: true });
  }, [activeTab, filters, userHasChangedDate, setSearchParams]);

  // Fetch report data on tab switch or filters change
  useEffect(() => {
    fetchActiveTabReport(activeTab);
  }, [activeTab, filters, fetchActiveTabReport]);

  // Auto-refresh interval timer
  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return;
    const intervalId = setInterval(() => {
      fetchActiveTabReport(activeTab);
    }, autoRefreshInterval * 1000);
    return () => clearInterval(intervalId);
  }, [autoRefreshInterval, activeTab, fetchActiveTabReport]);

  // Click outside listener for dropdowns
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.filter-dropdown-container')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handlers for Multi-Select Filters
  const toggleArrayFilter = (key: 'ticket_type' | 'status' | 'priority' | 'agent_ids' | 'team_ids' | 'source', item: string) => {
    const current = filters[key] || [];
    const exists = current.includes(item);
    const updated = exists ? current.filter((x) => x !== item) : [...current, item];
    setFilter(key, updated);
  };

  // Dispatch Summary Card Modal Submit
  const handleDispatchSummary = async () => {
    setIsDispatchingSummary(true);
    const success = await dispatchSummaryCard({
      channel: summaryChannel,
      webhook_url: summaryWebhookUrl || undefined,
      recipients: summaryEmail || undefined,
      customTitle: summaryCustomTitle,
      type: summaryType,
      schedule: scheduleEnabled
        ? {
            enabled: true,
            cron: scheduleCron,
            name: `${summaryCustomTitle} (${scheduleCron})`,
          }
        : undefined,
    });
    setIsDispatchingSummary(false);
    if (success) {
      if (scheduleEnabled) fetchSchedules();
      setShowSummaryCardModal(false);
    }
  };

  return (
    <div className="p-4 pb-16 space-y-4 bg-background min-h-screen text-foreground text-xs font-sans">
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* HEADER: TITLE & TOP ACTIONS                                    */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-card border border-border p-4 rounded-lg shadow-2xs">
        <div className="flex items-center gap-3">
          <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <BarChart3 className="size-5" />
          </div>
          <div>
            <h1 className="font-display text-page-title text-foreground tracking-tight">Support Analytics &amp; Reports</h1>
            <p className="text-xs text-muted-foreground">
              Real-time DB analytics for Ticket Volumes, Agent Performance, CSAT Satisfaction &amp; SLA Compliance
            </p>
          </div>
        </div>

        {/* Action Buttons: Summary Card + PDF / Excel / CSV Exports */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowSummaryCardModal(true)}
            className="text-xs font-semibold flex items-center gap-1.5 cursor-pointer shadow-2xs border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300 bg-blue-50/70 dark:bg-blue-950/40 hover:bg-blue-100"
          >
            <Send className="size-3.5 text-blue-600" />
            <span>Send Summary Card</span>
          </Button>

          {/* Single Export Dropdown with CSV, XLSX, and JSON */}
          <div className="relative">
            <Button
              variant="primary"
              size="sm"
              disabled={isExporting}
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="text-xs font-semibold flex items-center gap-1.5 cursor-pointer shadow-md bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Download className="size-3.5" />
              <span>Export</span>
              <ChevronDown className="size-3" />
            </Button>

            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 top-9 z-50 w-44 rounded-xl border border-border bg-card p-1 shadow-2xl text-xs space-y-0.5 animate-in fade-in zoom-in-95">
                  <button
                    type="button"
                    onClick={() => {
                      exportReportFile(activeTab === 'ticket' ? 'tickets' : activeTab === 'agent' ? 'agents' : activeTab, 'csv');
                      setShowExportMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left font-medium hover:bg-muted text-foreground transition-colors cursor-pointer"
                  >
                    <FileSpreadsheet className="size-3.5 text-blue-500" />
                    <span>CSV</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      exportReportFile(activeTab === 'ticket' ? 'tickets' : activeTab === 'agent' ? 'agents' : activeTab, 'xlsx');
                      setShowExportMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left font-medium hover:bg-muted text-foreground transition-colors cursor-pointer"
                  >
                    <FileSpreadsheet className="size-3.5 text-emerald-600" />
                    <span>Excel (.xlsx)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      exportReportFile(activeTab === 'ticket' ? 'tickets' : activeTab === 'agent' ? 'agents' : activeTab, 'json');
                      setShowExportMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left font-medium hover:bg-muted text-foreground transition-colors cursor-pointer"
                  >
                    <FileCode className="size-3.5 text-amber-500" />
                    <span>JSON</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* GRAFANA-STYLE GLOBAL STICKY FILTER BAR                          */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="sticky top-0 z-30 bg-card/95 backdrop-blur-md border border-border rounded-lg p-3 shadow-sm filter-dropdown-container">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-muted-foreground mr-1">
              <Filter className="size-3.5 text-emerald-500" />
              <span className="font-semibold text-foreground uppercase tracking-wider text-[11px]">FILTERS:</span>
            </div>

            {/* Custom Time Range Picker */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTimePicker(!showTimePicker)}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1 text-xs text-foreground hover:bg-muted/70 transition-colors shadow-2xs cursor-pointer",
                  (showTimePicker || (filters.from && filters.to)) && "border-[#10b981] ring-1 ring-[#10b981]/30"
                )}
                title="Select time range"
              >
                <Clock className="size-3.5 text-muted-foreground" />
                <span className="font-mono text-[11px] text-foreground">
                  {filters.from && filters.to
                    ? `${formatDisplayDateTime(filters.from, false)} - ${formatDisplayDateTime(filters.to, true)}`
                    : '--:-- - --:--'}
                </span>
                <Clock className="size-3 text-muted-foreground" />
              </button>

              {showTimePicker && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowTimePicker(false)} />
                  <div className="absolute left-0 top-9 z-50 w-[620px] max-w-[95vw] rounded-xl border border-border bg-card shadow-2xl p-0 overflow-hidden animate-in fade-in zoom-in-95 text-xs">
                    {/* Header bar matching Image 2 */}
                    <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleShiftTimeWindow('back')}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                          title="Move time window backward"
                        >
                          <ChevronLeft className="size-4" />
                        </button>
                        <div
                          onClick={() => setShowTimePicker(false)}
                          className="flex items-center gap-1.5 font-mono text-[11px] font-semibold text-foreground px-2 py-0.5 rounded hover:bg-muted cursor-pointer transition-colors"
                        >
                          <Clock className="size-3.5 text-muted-foreground" />
                          <span>
                            {formatDisplayDateTime(fromInput, false)} to {formatDisplayDateTime(toInput, true)}{' '}
                            <span className="text-warning">({systemTimeZone})</span>
                          </span>
                          <ChevronUp className="size-3.5 text-muted-foreground ml-1" />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleShiftTimeWindow('forward')}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                          title="Move time window forward"
                        >
                          <ChevronRight className="size-4" />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={handleZoomOut}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                        title="Zoom out (double range)"
                      >
                        <ZoomOut className="size-4" />
                      </button>
                    </div>

                    {/* Popover 2-column body matching Image 2 */}
                    <div className="grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-border">
                      {/* Left Column: Absolute time range (col-span-7) */}
                      <div className="md:col-span-7 p-3.5 space-y-3">
                        <h4 className="font-semibold text-xs text-foreground tracking-tight">
                          Absolute time range
                        </h4>

                        <div className="space-y-1">
                          <label className="text-[11px] font-medium text-muted-foreground block">
                            From
                          </label>
                          <div className="relative flex items-center">
                            <input
                              type="text"
                              value={fromInput}
                              onChange={(e) => setFromInput(e.target.value)}
                              placeholder="YYYY-MM-DD HH:mm:ss"
                              className="w-full bg-background border border-input rounded-md px-2.5 py-1.5 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary pr-8"
                            />
                            <Calendar className="absolute right-2.5 size-3.5 text-muted-foreground pointer-events-none" />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-medium text-muted-foreground block">
                            To
                          </label>
                          <div className="relative flex items-center">
                            <input
                              type="text"
                              value={toInput}
                              onChange={(e) => setToInput(e.target.value)}
                              placeholder="YYYY-MM-DD HH:mm:ss"
                              className="w-full bg-background border border-input rounded-md px-2.5 py-1.5 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary pr-8"
                            />
                            <Calendar className="absolute right-2.5 size-3.5 text-muted-foreground pointer-events-none" />
                          </div>
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(`${fromInput} to ${toInput}`);
                              toast.success('Copied time range to clipboard');
                            }}
                            className="p-1.5 rounded-md border border-input bg-card hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors shadow-2xs"
                            title="Copy time range"
                          >
                            <Copy className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const text = await navigator.clipboard.readText();
                                const parts = text.split(/\s+to\s+/i);
                                if (parts.length === 2) {
                                  setFromInput(parts[0].trim());
                                  setToInput(parts[1].trim());
                                  toast.success('Pasted time range');
                                } else {
                                  toast.error('Clipboard text format should be: From to To');
                                }
                              } catch {
                                toast.error('Unable to read clipboard');
                              }
                            }}
                            className="p-1.5 rounded-md border border-input bg-card hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors shadow-2xs"
                            title="Paste time range"
                          >
                            <ClipboardPaste className="size-3.5" />
                          </button>

                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleApplyAbsoluteRange}
                            className="text-xs font-semibold px-4 cursor-pointer"
                          >
                            Apply time range
                          </Button>
                        </div>

                        {/* Recently used absolute ranges */}
                        <div className="pt-2 border-t border-border/60">
                          <h5 className="font-medium text-[11px] text-muted-foreground mb-1.5">
                            Recently used absolute ranges
                          </h5>
                          <div className="space-y-1">
                            {recentRanges.map((r, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  const parts = r.split(' to ');
                                  if (parts.length === 2) {
                                    setFromInput(parts[0].trim());
                                    setToInput(parts[1].trim());
                                    setUserHasChangedDate(true);
                                    setFilters({ date_range: 'custom', from: parts[0].trim(), to: parts[1].trim() });
                                    setShowTimePicker(false);
                                  }
                                }}
                                className="w-full text-left font-mono text-[11px] text-foreground hover:text-primary hover:bg-muted/60 px-1.5 py-1 rounded transition-colors block truncate"
                              >
                                {r}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right Column: Search quick ranges (col-span-5) */}
                      <div className="md:col-span-5 p-3.5 bg-muted/10 space-y-2">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground" />
                          <input
                            type="text"
                            placeholder="Search quick ranges"
                            value={quickSearchQuery}
                            onChange={(e) => setQuickSearchQuery(e.target.value)}
                            className="w-full bg-background border border-input rounded-md pl-7 pr-2.5 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </div>

                        <div className="max-h-64 overflow-y-auto custom-scrollbar space-y-0.5">
                          {QUICK_RANGES
                            .filter((r) => r.label.toLowerCase().includes(quickSearchQuery.toLowerCase()))
                            .map((r) => (
                              <button
                                key={r.label}
                                type="button"
                                onClick={() => handleApplyQuickRange(r)}
                                className="w-full text-left px-2 py-1 rounded text-xs text-foreground hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer"
                              >
                                {r.label}
                              </button>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Group By Selector */}
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-muted-foreground font-semibold">Group By:</span>
              <select
                value={filters.group_by || 'day'}
                onChange={(e) => setFilter('group_by', e.target.value as any)}
                className="bg-background border border-border rounded-lg px-2 py-1 text-[11px] font-semibold text-foreground cursor-pointer focus:outline-none"
              >
                <option value="hour">Hour</option>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>

            {/* Ticket Type Multi-select */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenDropdown(openDropdown === 'type' ? null : 'type')}
                className={cn(
                  'flex items-center gap-1.5 bg-background border border-border rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors cursor-pointer',
                  filters.ticket_type && filters.ticket_type.length > 0
                    ? 'border-primary/50 text-primary bg-primary/5'
                    : 'text-foreground hover:bg-muted'
                )}
              >
                <span>Type {filters.ticket_type?.length ? `(${filters.ticket_type.length})` : ''}</span>
                <ChevronDown className="size-3 text-muted-foreground" />
              </button>

              {openDropdown === 'type' && (
                <div className="absolute left-0 top-8 z-50 w-48 bg-card border border-border rounded-lg shadow-xl p-1.5 space-y-0.5 animate-in fade-in">
                  {['Incident', 'Service Request', 'Change Request', 'Problem', 'Access Request'].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleArrayFilter('ticket_type', t)}
                      className="w-full flex items-center justify-between px-2 py-1 rounded text-left hover:bg-muted text-[11px] font-medium"
                    >
                      <span>{t}</span>
                      {filters.ticket_type?.includes(t) && <Check className="size-3.5 text-primary" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Status Multi-select */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
                className={cn(
                  'flex items-center gap-1.5 bg-background border border-border rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors cursor-pointer',
                  filters.status && filters.status.length > 0
                    ? 'border-primary/50 text-primary bg-primary/5'
                    : 'text-foreground hover:bg-muted'
                )}
              >
                <span>Status {filters.status?.length ? `(${filters.status.length})` : ''}</span>
                <ChevronDown className="size-3 text-muted-foreground" />
              </button>

              {openDropdown === 'status' && (
                <div className="absolute left-0 top-8 z-50 w-44 bg-card border border-border rounded-lg shadow-xl p-1.5 space-y-0.5 animate-in fade-in">
                  {['New', 'In Progress', 'Resolved', 'Closed', 'Pending'].map((st) => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => toggleArrayFilter('status', st)}
                      className="w-full flex items-center justify-between px-2 py-1 rounded text-left hover:bg-muted text-[11px] font-medium"
                    >
                      <span>{st}</span>
                      {filters.status?.includes(st) && <Check className="size-3.5 text-primary" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Priority Multi-select */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenDropdown(openDropdown === 'priority' ? null : 'priority')}
                className={cn(
                  'flex items-center gap-1.5 bg-background border border-border rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors cursor-pointer',
                  filters.priority && filters.priority.length > 0
                    ? 'border-primary/50 text-primary bg-primary/5'
                    : 'text-foreground hover:bg-muted'
                )}
              >
                <span>Priority {filters.priority?.length ? `(${filters.priority.length})` : ''}</span>
                <ChevronDown className="size-3 text-muted-foreground" />
              </button>

              {openDropdown === 'priority' && (
                <div className="absolute left-0 top-8 z-50 w-40 bg-card border border-border rounded-lg shadow-xl p-1.5 space-y-0.5 animate-in fade-in">
                  {['Urgent', 'High', 'Medium', 'Low'].map((prio) => (
                    <button
                      key={prio}
                      type="button"
                      onClick={() => toggleArrayFilter('priority', prio)}
                      className="w-full flex items-center justify-between px-2 py-1 rounded text-left hover:bg-muted text-[11px] font-medium"
                    >
                      <span>{prio}</span>
                      {filters.priority?.includes(prio) && <Check className="size-3.5 text-primary" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Agents Multi-select */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenDropdown(openDropdown === 'agents' ? null : 'agents')}
                className={cn(
                  'flex items-center gap-1.5 bg-background border border-border rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors cursor-pointer',
                  filters.agent_ids && filters.agent_ids.length > 0
                    ? 'border-primary/50 text-primary bg-primary/5'
                    : 'text-foreground hover:bg-muted'
                )}
              >
                <span>Agents {filters.agent_ids?.length ? `(${filters.agent_ids.length})` : ''}</span>
                <ChevronDown className="size-3 text-muted-foreground" />
              </button>

              {openDropdown === 'agents' && (
                <div className="absolute left-0 top-8 z-50 w-52 max-h-64 overflow-y-auto bg-card border border-border rounded-lg shadow-xl p-1.5 space-y-0.5 animate-in fade-in">
                  {storeAgents.map((ag) => {
                    const agentIdStr = String(ag.id || (ag as any).profile_id);
                    const agentName = `${ag.first_name || ''} ${ag.last_name || ''}`.trim() || 'Support Agent';
                    return (
                      <button
                        key={agentIdStr}
                        type="button"
                        onClick={() => toggleArrayFilter('agent_ids', agentIdStr)}
                        className="w-full flex items-center justify-between px-2 py-1 rounded text-left hover:bg-muted text-[11px] font-medium"
                      >
                        <span className="truncate">{agentName}</span>
                        {filters.agent_ids?.includes(agentIdStr) && <Check className="size-3.5 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Teams Multi-select */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenDropdown(openDropdown === 'teams' ? null : 'teams')}
                className={cn(
                  'flex items-center gap-1.5 bg-background border border-border rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors cursor-pointer',
                  filters.team_ids && filters.team_ids.length > 0
                    ? 'border-primary/50 text-primary bg-primary/5'
                    : 'text-foreground hover:bg-muted'
                )}
              >
                <span>Teams {filters.team_ids?.length ? `(${filters.team_ids.length})` : ''}</span>
                <ChevronDown className="size-3 text-muted-foreground" />
              </button>

              {openDropdown === 'teams' && (
                <div className="absolute left-0 top-8 z-50 w-52 max-h-64 overflow-y-auto bg-card border border-border rounded-lg shadow-xl p-1.5 space-y-0.5 animate-in fade-in">
                  {storeQueues.map((q) => {
                    const queueIdStr = String(q.id);
                    return (
                      <button
                        key={queueIdStr}
                        type="button"
                        onClick={() => toggleArrayFilter('team_ids', queueIdStr)}
                        className="w-full flex items-center justify-between px-2 py-1 rounded text-left hover:bg-muted text-[11px] font-medium"
                      >
                        <span className="truncate">{q.name}</span>
                        {filters.team_ids?.includes(queueIdStr) && <Check className="size-3.5 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Controls: Auto-Refresh, Manual Refresh, Reset */}
          <div className="flex items-center gap-2">
            {/* Auto-Refresh Interval */}
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-muted-foreground font-semibold">Auto-Refresh:</span>
              <select
                value={autoRefreshInterval}
                onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
                className="bg-background border border-border rounded-lg px-2 py-1 text-[11px] font-semibold text-foreground cursor-pointer focus:outline-none"
              >
                <option value={0}>Off</option>
                <option value={30}>30s</option>
                <option value={60}>1m</option>
                <option value={300}>5m</option>
              </select>
            </div>

            {/* Manual Refresh Button */}
            <button
              type="button"
              onClick={() => fetchActiveTabReport(activeTab)}
              disabled={isLoading}
              title="Refresh Current Report"
              className="p-1.5 bg-background border border-border rounded-lg text-foreground hover:bg-muted transition-colors cursor-pointer"
            >
              <RefreshCw className={cn('size-3.5', isLoading && 'animate-spin text-primary')} />
            </button>

            {/* Reset Filters Button */}
            <button
              type="button"
              onClick={() => {
                setUserHasChangedDate(false);
                resetFilters();
                const init = getInitialSystemDateRange();
                setFromInput(init.from);
                setToInput(init.to);
              }}
              title="Reset all filters to defaults"
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground font-medium px-2 py-1 rounded hover:bg-muted transition-colors cursor-pointer"
            >
              <RotateCcw className="size-3" />
              <span>Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 4 TABS NAVIGATION                                               */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="border-b border-border">
        <div className="flex items-center gap-6 overflow-x-auto scrollbar-none">
          {[
            { id: 'ticket', label: 'Ticket Reports', icon: TicketIcon },
            { id: 'agent', label: 'Agent Performance', icon: Users },
            { id: 'csat', label: 'CSAT Reports', icon: Star },
            { id: 'sla', label: 'SLA Reports', icon: ShieldCheck },
          ].map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id as any)}
                className={cn(
                  'inline-flex items-center gap-2 py-2.5 px-1 border-b-2 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap',
                  isActive
                    ? 'border-primary text-primary font-semibold -mb-px'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="size-3.5 shrink-0" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* TAB 1: TICKET REPORTS                                           */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'ticket' && (
        <div className="space-y-6 animate-in fade-in">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Tickets Created</span>
                <p className="text-3xl font-semibold text-foreground">
                  {ticketReport ? ticketReport.totals.totalCreated : '—'}
                </p>
                <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                  <TrendingUp className="size-3" /> Live filtered DB volume
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Tickets Resolved</span>
                <p className="text-3xl font-semibold text-emerald-600 dark:text-emerald-400">
                  {ticketReport ? ticketReport.totals.totalResolved : '—'}
                </p>
                <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                  <CheckCircle2 className="size-3" /> Fulfilled and closed
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Avg Resolution Time</span>
                <p className="text-3xl font-semibold text-primary">
                  {ticketReport ? ticketReport.totals.avgResolutionTime : '—'}
                </p>
                <p className="text-xs text-muted-foreground font-medium">From creation to resolution</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Active / In Progress</span>
                <p className="text-3xl font-semibold text-amber-600 dark:text-amber-400">
                  {ticketReport ? ticketReport.totals.activeCount : '—'}
                </p>
                <p className="text-xs text-muted-foreground font-medium">Currently assigned to engineers</p>
              </CardContent>
            </Card>
          </div>

          {/* Created vs Resolved Trend Chart */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border flex justify-between items-center">
              <div>
                <h2 className="font-semibold text-foreground text-sm">Ticket Volume Trend Over Time</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Created tickets vs resolved tickets grouped by {filters.group_by || 'day'}
                </p>
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {ticketReport?.trend.length || 0} Data Points
              </Badge>
            </CardHeader>
            <CardContent className="p-6">
              {ticketReport && ticketReport.trend.length > 0 ? (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={ticketReport.trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="createdGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563EB" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#2563EB" stopOpacity={0.0} />
                        </linearGradient>
                        <linearGradient id="resolvedGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#16A34A" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#16A34A" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} />
                      <YAxis stroke="#64748B" fontSize={11} tickLine={false} allowDecimals={false} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: '#0F172A',
                          borderRadius: '8px',
                          color: '#F8FAFC',
                          fontSize: '12px',
                        }}
                      />
                      <Area type="monotone" dataKey="created" name="Created" stroke="#2563EB" strokeWidth={2} fill="url(#createdGrad)" />
                      <Area type="monotone" dataKey="resolved" name="Resolved" stroke="#16A34A" strokeWidth={2} fill="url(#resolvedGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="py-12 text-center text-muted-foreground">No volume trend data found for current filters.</div>
              )}
            </CardContent>
          </Card>

          {/* Ticket Types Breakdown Grid */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border flex justify-between items-center">
              <div>
                <h2 className="font-semibold text-foreground text-sm">Ticket Types &amp; Sequential Format Breakdown</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Prefix formats, ticket counts and SLA compliance</p>
              </div>
              <Badge variant="secondary" className="font-mono text-xs">
                {ticketReport?.byType.length || 0} Configured Types
              </Badge>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ticketReport?.byType.map((t) => (
                  <div key={t.name} className="p-3.5 rounded-xl border border-border bg-card shadow-2xs flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs font-semibold px-1.5 py-0.5 rounded bg-muted border border-border">
                          {t.prefix}
                        </span>
                        <p className="font-semibold text-xs text-foreground">{t.name}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        SLA Met: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{t.sla}</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-foreground">{t.count}</p>
                      <p className="text-[11px] text-muted-foreground">{t.pct}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Priorities & Backlog Aging Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Priority */}
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border">
                <h2 className="font-semibold text-foreground text-sm">Tickets by Priority Tier</h2>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                {ticketReport?.byPriority.map((p, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${p.color} shrink-0`} />
                    <div className="flex-1 h-2 bg-background border border-border/60 rounded-full overflow-hidden">
                      <div className={`h-full ${p.color} rounded-full`} style={{ width: `${Math.max(p.pct, 4)}%` }} />
                    </div>
                    <div className="text-right text-xs w-28 font-mono">
                      <span className="font-semibold text-foreground">{p.count}</span>
                      <span className="text-muted-foreground ml-1">({p.pct}%) {p.priority}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Backlog Aging */}
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border">
                <h2 className="font-semibold text-foreground text-sm">Active Ticket Backlog Aging</h2>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                {ticketReport?.backlogAging.map((b, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="text-xs font-semibold text-muted-foreground w-28 shrink-0">{b.range}</span>
                    <div className="flex-1 h-2 bg-background border border-border/60 rounded-full overflow-hidden mx-3">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          i === 0 ? 'bg-emerald-500' : i === 1 ? 'bg-primary' : i === 2 ? 'bg-amber-500' : 'bg-rose-500'
                        )}
                        style={{ width: `${Math.max(b.pct, 3)}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono font-semibold text-foreground w-16 text-right">
                      {b.count} ({b.pct}%)
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* TAB 2: AGENT PERFORMANCE                                        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'agent' && (
        <div className="space-y-6 animate-in fade-in">
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Active Engineers</span>
                <p className="text-3xl font-semibold text-foreground">{agentReport?.summary.totalAgents || 0}</p>
                <p className="text-xs text-muted-foreground">Certified support desk profiles</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Assigned Workload</span>
                <p className="text-3xl font-semibold text-primary">{agentReport?.summary.totalAssigned || 0}</p>
                <p className="text-xs text-muted-foreground">Tickets allocated across team</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Overall Team SLA Met</span>
                <p className="text-3xl font-semibold text-emerald-600">{agentReport?.summary.avgSlaMet || '—'}</p>
                <p className="text-xs text-emerald-600 font-semibold">Resolution compliance</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Team Average CSAT</span>
                <p className="text-3xl font-semibold text-amber-500">★ {agentReport?.summary.avgCsat || '5.0'} / 5.0</p>
                <p className="text-xs text-muted-foreground">Customer satisfaction average</p>
              </CardContent>
            </Card>
          </div>

          {/* Performance Matrix Table */}
          <Card className="bg-card border-border shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border bg-muted/20 flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                IT Support Team Performance Matrix
              </span>
              <span className="text-xs text-muted-foreground">Ranked by Assigned &amp; Resolved Volumes</span>
            </div>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-xs text-muted-foreground font-semibold bg-muted/10 border-b border-border">
                    <th className="text-left px-6 py-3.5">Support Engineer</th>
                    <th className="text-center px-4 py-3.5">Assigned</th>
                    <th className="text-center px-4 py-3.5">Resolved</th>
                    <th className="text-center px-4 py-3.5">Active</th>
                    <th className="text-center px-4 py-3.5">Avg Resolution</th>
                    <th className="text-center px-4 py-3.5">First Response</th>
                    <th className="text-center px-4 py-3.5">SLA Met</th>
                    <th className="text-center px-4 py-3.5">CSAT Score</th>
                    <th className="text-center px-4 py-3.5">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {agentReport?.agents.map((agent) => (
                    <tr key={agent.agentId} className="hover:bg-accent/40 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center font-semibold text-primary text-xs">
                            {agent.name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-foreground">{agent.name}</p>
                            <p className="text-[11px] text-muted-foreground font-mono">{agent.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center font-mono font-semibold text-foreground">{agent.assigned}</td>
                      <td className="px-4 py-4 text-center font-semibold text-emerald-600 dark:text-emerald-400">
                        {agent.resolved}
                      </td>
                      <td className="px-4 py-4 text-center font-mono text-muted-foreground">{agent.active}</td>
                      <td className="px-4 py-4 text-center text-muted-foreground font-mono">{agent.avgResolutionTime}</td>
                      <td className="px-4 py-4 text-center text-muted-foreground font-mono">{agent.avgFirstResponse}</td>
                      <td className="px-4 py-4 text-center">
                        <span
                          className={cn(
                            'font-semibold font-mono',
                            parseFloat(agent.slaMetPct) >= 90 ? 'text-emerald-600' : 'text-amber-600'
                          )}
                        >
                          {agent.slaMetPct}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center font-semibold text-amber-500">★ {agent.csatAvg}</td>
                      <td className="px-4 py-4 text-center font-semibold text-emerald-600">{agent.trend}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* TAB 3: CSAT REPORTS                                             */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'csat' && (
        <div className="space-y-6 animate-in fade-in">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Average CSAT</span>
                <p className="text-3xl font-semibold text-amber-500">★ {csatReport?.avgScore || '—'} / 5.0</p>
                <p className="text-xs text-emerald-600 font-semibold">Positive customer sentiment</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Surveys Sent</span>
                <p className="text-3xl font-semibold text-foreground">{csatReport?.totalSurveysSent || 0}</p>
                <p className="text-xs text-muted-foreground font-medium">Post-resolution triggers</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Response Rate</span>
                <p className="text-3xl font-semibold text-emerald-600">{csatReport?.responseRate || '—'}</p>
                <p className="text-xs text-muted-foreground font-medium">{csatReport?.totalResponses || 0} Responses received</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Net Promoter Score</span>
                <p className="text-3xl font-semibold text-primary">{csatReport?.npsScore || '—'}</p>
                <p className="text-xs text-emerald-600 font-semibold">World-class benchmark</p>
              </CardContent>
            </Card>
          </div>

          {/* Rating Distribution */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground text-sm">Customer Satisfaction Rating Distribution</h2>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {csatReport?.ratingDistribution.map((r, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-muted-foreground w-36 shrink-0">{r.label}</span>
                  <div className="flex-1 h-3.5 bg-background border border-border/60 rounded-xl overflow-hidden">
                    <div
                      className={cn('h-full rounded-xl transition-all duration-700', r.color)}
                      style={{ width: `${Math.max(r.pct, 2)}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono font-semibold text-foreground w-16 text-right">
                    {r.count} ({r.pct}%)
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Recent Comments Feed */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border flex justify-between items-center">
              <h2 className="font-semibold text-foreground text-sm">Recent Customer Feedback &amp; Comments</h2>
              <Badge variant="outline" className="font-mono text-xs">
                {csatReport?.recentComments.length || 0} Comments
              </Badge>
            </CardHeader>
            <CardContent className="p-6 divide-y divide-border/60 space-y-4">
              {csatReport && csatReport.recentComments.length > 0 ? (
                csatReport.recentComments.map((c) => (
                  <div key={c.id} className="pt-4 first:pt-0 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-primary">{c.ticketNo}</span>
                        <span className="text-amber-500 font-semibold">★ {c.rating}.0</span>
                        <span className="text-muted-foreground">· {c.agentName} ({c.teamName})</span>
                      </div>
                      <span className="text-[11px] text-muted-foreground font-mono">
                        {new Date(c.submittedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-foreground italic bg-muted/20 p-2.5 rounded-lg border border-border/50">
                      &ldquo;{c.comment || 'No comment provided'}&rdquo;
                    </p>
                  </div>
                ))
              ) : (
                <div className="py-6 text-center text-muted-foreground">No recent feedback comments found.</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* TAB 4: SLA REPORTS                                              */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'sla' && (
        <div className="space-y-6 animate-in fade-in">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Overall SLA Compliance</span>
                <p className="text-3xl font-semibold text-emerald-600">{slaReport?.overallCompliance || '—'}</p>
                <p className="text-xs text-emerald-600 font-semibold">{slaReport?.totalMet || 0} Requests Met Target</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Total Breaches</span>
                <p className="text-3xl font-semibold text-rose-600">{slaReport?.totalBreached || 0}</p>
                <p className="text-xs text-muted-foreground font-medium">Under active remediation</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Total Requests Tracked</span>
                <p className="text-3xl font-semibold text-blue-600">{slaReport?.totalRequests || 0}</p>
                <p className="text-xs text-muted-foreground font-medium">Matching current filter parameters</p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-xs">
              <CardContent className="p-5 space-y-1">
                <span className="text-xs text-muted-foreground font-semibold uppercase">Urgent Escalations</span>
                <p className="text-3xl font-semibold text-amber-600">{slaReport?.urgentEscalations || 0}</p>
                <p className="text-xs text-muted-foreground font-medium">Auto-routed &amp; monitored</p>
              </CardContent>
            </Card>
          </div>

          {/* 1. SLA Performance by Department Team Queue Bar Chart & Table */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-foreground text-sm flex items-center gap-2">
                  <Users className="size-4 text-primary" />
                  SLA Performance by Department Team Queue
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Resolution and response SLA targets tracked per operational department queue
                </p>
              </div>
              <div className="flex items-center gap-2.5 self-start sm:self-auto">
                <div className="flex items-center bg-muted/60 p-0.5 rounded-lg border border-border">
                  <button
                    type="button"
                    onClick={() => setQueueViewMode('chart')}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer',
                      queueViewMode === 'chart'
                        ? 'bg-card text-foreground shadow-2xs font-semibold'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <BarChart3 className="size-3.5 text-primary" />
                    <span>Bar Chart</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setQueueViewMode('table')}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer',
                      queueViewMode === 'table'
                        ? 'bg-card text-foreground shadow-2xs font-semibold'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <TableIcon className="size-3.5 text-muted-foreground" />
                    <span>Table</span>
                  </button>
                </div>
                <Badge variant="outline" className="text-xs font-mono">
                  {slaReport?.byDepartment.length || 0} Queues Tracked (Sum: {slaReport?.totalRequests || 0})
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {queueViewMode === 'chart' ? (
                <div className="p-6 space-y-6">
                  {slaReport?.byDepartment && slaReport.byDepartment.some((d) => d.total > 0) ? (
                    <>
                      <div className="h-72 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={slaReport.byDepartment}
                            margin={{ top: 15, right: 20, left: -10, bottom: 25 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" opacity={0.6} />
                            <XAxis
                              dataKey="label"
                              stroke="#64748B"
                              fontSize={11}
                              tickLine={false}
                              axisLine={{ stroke: '#E2E8F0' }}
                              interval={0}
                              tick={({ x, y, payload }) => (
                                <g transform={`translate(${x},${y})`}>
                                  <text
                                    x={0}
                                    y={0}
                                    dy={16}
                                    textAnchor="middle"
                                    fill="#475569"
                                    fontSize={11}
                                    fontWeight={500}
                                  >
                                    {payload.value.replace(' Queue', '')}
                                  </text>
                                </g>
                              )}
                            />
                            <YAxis
                              stroke="#64748B"
                              fontSize={11}
                              tickLine={false}
                              axisLine={{ stroke: '#E2E8F0' }}
                              allowDecimals={false}
                            />
                            <RechartsTooltip
                              content={({ active, payload }) => {
                                if (!active || !payload || !payload.length) return null;
                                const data = payload[0].payload as SlaQueueItem;
                                return (
                                  <div className="bg-[#0F172A] text-[#F8FAFC] rounded-lg p-3 text-xs shadow-xl border border-slate-800 space-y-2 min-w-[210px]">
                                    <div className="flex items-center justify-between border-b border-slate-800 pb-1.5">
                                      <span className="font-semibold text-white">{data.label}</span>
                                      <span className="font-mono text-[11px] text-slate-400">{data.total} total</span>
                                    </div>
                                    <div className="space-y-1">
                                      <div className="flex items-center justify-between">
                                        <span className="flex items-center gap-1.5 text-emerald-400">
                                          <span className="size-2 rounded-full bg-emerald-500" />
                                          SLA Met
                                        </span>
                                        <span className="font-mono font-medium text-emerald-400">{data.met}</span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="flex items-center gap-1.5 text-rose-400">
                                          <span className="size-2 rounded-full bg-rose-500" />
                                          SLA Breached
                                        </span>
                                        <span className="font-mono font-medium text-rose-400">{data.breached}</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between pt-1.5 border-t border-slate-800 font-medium">
                                      <span className="text-slate-300">Compliance Rate</span>
                                      <span
                                        className={cn(
                                          'font-mono font-bold',
                                          data.compliance >= 90
                                            ? 'text-emerald-400'
                                            : data.compliance >= 75
                                            ? 'text-amber-400'
                                            : 'text-rose-400'
                                        )}
                                      >
                                        {data.compliance}%
                                      </span>
                                    </div>
                                  </div>
                                );
                              }}
                            />
                            <Legend
                              verticalAlign="top"
                              align="right"
                              iconType="circle"
                              wrapperStyle={{ paddingBottom: 12, fontSize: 12 }}
                            />
                            <Bar
                              dataKey="met"
                              name="SLA Met"
                              fill="#16A34A"
                              radius={[4, 4, 0, 0]}
                              maxBarSize={36}
                            />
                            <Bar
                              dataKey="breached"
                              name="SLA Breached"
                              fill="#DC2626"
                              radius={[4, 4, 0, 0]}
                              maxBarSize={36}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Scannable Department Queue Cards */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-3 border-t border-border/60">
                        {slaReport.byDepartment.map((dept) => (
                          <div
                            key={dept.key}
                            className="p-2.5 rounded-lg bg-muted/20 border border-border/60 text-xs space-y-1.5"
                          >
                            <div className="flex items-center gap-1.5 truncate">
                              <span className="size-2 rounded-full bg-primary shrink-0" />
                              <span className="font-semibold text-foreground truncate" title={dept.label}>
                                {dept.label.replace(' Queue', '')}
                              </span>
                            </div>
                            <div className="flex items-center justify-between font-mono">
                              <span className="text-muted-foreground">{dept.total} reqs</span>
                              <span
                                className={cn(
                                  'font-bold text-[11px] px-1.5 py-0.5 rounded',
                                  dept.compliance >= 90
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : dept.compliance >= 75
                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                    : 'bg-rose-50 text-rose-700 border border-rose-200'
                                )}
                              >
                                {dept.compliance}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="py-16 text-center text-muted-foreground">
                      <p className="font-semibold">No requests match current filter criteria</p>
                      <p className="text-xs mt-1">Total requests: 0</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-muted-foreground font-semibold uppercase tracking-wider">
                        <th className="px-6 py-3">Department Queue</th>
                        <th className="px-4 py-3 text-center">Total Requests</th>
                        <th className="px-4 py-3 text-center text-emerald-600">SLA Met</th>
                        <th className="px-4 py-3 text-center text-rose-600">SLA Breached</th>
                        <th className="px-6 py-3 text-right">Compliance Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {slaReport?.byDepartment.map((dept) => (
                        <tr key={dept.key} className="hover:bg-muted/20 transition-colors">
                          <td className="px-6 py-3.5 font-semibold text-foreground flex items-center gap-2">
                            <span className="size-2 rounded-full bg-primary" />
                            {dept.label}
                          </td>
                          <td className="px-4 py-3.5 text-center font-mono font-medium text-foreground">{dept.total}</td>
                          <td className="px-4 py-3.5 text-center font-mono font-semibold text-emerald-600">{dept.met}</td>
                          <td className="px-4 py-3.5 text-center font-mono font-semibold text-rose-600">{dept.breached}</td>
                          <td className="px-6 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={cn(
                                    'h-full rounded-full transition-all',
                                    dept.compliance >= 90
                                      ? 'bg-emerald-500'
                                      : dept.compliance >= 75
                                      ? 'bg-amber-500'
                                      : 'bg-rose-500'
                                  )}
                                  style={{ width: `${dept.compliance}%` }}
                                />
                              </div>
                              <span
                                className={cn(
                                  'font-mono font-semibold min-w-[42px] text-right',
                                  dept.compliance >= 90
                                    ? 'text-emerald-600'
                                    : dept.compliance >= 75
                                    ? 'text-amber-600'
                                    : 'text-rose-600'
                                )}
                              >
                                {dept.compliance}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 2. SLA Compliance by Priority Tier & System Generated vs Direct Entry */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Donut Chart 1: SLA Compliance Breakdown by Priority Tier */}
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-foreground text-sm flex items-center gap-2">
                      <ShieldAlert className="size-4 text-primary" />
                      SLA Compliance Breakdown by Priority Tier
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Slice = Requests per priority, legend = Actual vs Target compliance
                    </p>
                  </div>
                  <Badge variant="outline" className="font-mono text-xs">
                    Sum: {slaReport?.byPriority.reduce((s, p) => s + p.total, 0) || 0}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {slaReport && slaReport.byPriority.some((p) => p.total > 0) ? (
                  <div className="space-y-4">
                    <div className="h-56 w-full flex items-center justify-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={slaReport.byPriority.filter((p) => p.total > 0)}
                            dataKey="total"
                            nameKey="priority"
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={80}
                            paddingAngle={3}
                          >
                            {slaReport.byPriority
                              .filter((p) => p.total > 0)
                              .map((entry) => (
                                <Cell key={`prio-${entry.priority}`} fill={PRIORITY_COLORS[entry.priority] || '#475569'} />
                              ))}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value: any, name: any, item: any) => [
                              `${value} requests · ${item.payload.compliance}% met (target ${item.payload.target}%)`,
                              `${name} priority`,
                            ]}
                            contentStyle={{
                              backgroundColor: '#0F172A',
                              borderRadius: '8px',
                              color: '#F8FAFC',
                              fontSize: '12px',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Legend: Reqs / Actual vs Target / Breaches */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-border/60">
                      {slaReport.byPriority.map((p) => {
                        const onTarget = p.compliance >= p.target;
                        return (
                          <div key={p.priority} className="flex items-center justify-between p-2 rounded-lg bg-muted/20 border border-border/40 text-xs">
                            <div className="flex items-center gap-2 truncate pr-2">
                              <span
                                className="size-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: PRIORITY_COLORS[p.priority] || '#475569' }}
                              />
                              <span className="font-semibold text-foreground truncate">{p.priority}</span>
                            </div>
                            <div className="font-mono font-medium shrink-0 text-right">
                              <span className="text-foreground">{p.total} reqs</span>
                              <span className={cn('ml-1.5', onTarget ? 'text-emerald-600' : 'text-rose-600')}>
                                {p.compliance}%
                              </span>
                              <span className="text-muted-foreground"> / {p.target}%</span>
                              {p.breaches > 0 && <span className="text-rose-600 ml-1.5">({p.breaches} br.)</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="py-16 text-center text-muted-foreground">
                    <p className="font-semibold">No requests match current filter criteria</p>
                    <p className="text-xs mt-1">Total requests: 0</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Donut Chart 2: SLA: System Generated vs Direct Entry */}
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="bg-muted/20 px-6 py-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-foreground text-sm flex items-center gap-2">
                      <Bot className="size-4 text-primary" />
                      SLA: System Generated vs Direct Entry
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Slice = Requests per source, legend = Reqs / Met / % compliance
                    </p>
                  </div>
                  <Badge variant="outline" className="font-mono text-xs">
                    Sum: {slaReport?.bySource.reduce((s, src) => s + src.total, 0) || 0}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {slaReport && slaReport.totalRequests > 0 ? (
                  <div className="space-y-4">
                    <div className="h-56 w-full flex items-center justify-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={slaReport.bySource.filter((s) => s.total > 0)}
                            dataKey="total"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={80}
                            paddingAngle={3}
                          >
                            {slaReport.bySource
                              .filter((s) => s.total > 0)
                              .map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={DONUT_COLORS[(index + 2) % DONUT_COLORS.length]} />
                              ))}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value: any, name: any, item: any) => [
                              `${value} requests (${item.payload.compliance}% Met)`,
                              name,
                            ]}
                            contentStyle={{
                              backgroundColor: '#0F172A',
                              borderRadius: '8px',
                              color: '#F8FAFC',
                              fontSize: '12px',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Detailed Legend with Reqs / Met / Compliance */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-border/60">
                      {slaReport.bySource.map((src, idx) => (
                        <div key={src.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/20 border border-border/40 text-xs">
                          <div className="flex items-center gap-2 truncate pr-2">
                            <span
                              className="size-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: DONUT_COLORS[(idx + 2) % DONUT_COLORS.length] }}
                            />
                            <span className="font-semibold text-foreground truncate">{src.label.split('(')[0]}</span>
                          </div>
                          <div className="font-mono font-medium shrink-0 text-right">
                            <span className="text-foreground">{src.total} reqs</span>
                            <span className="text-emerald-600 ml-1.5">{src.compliance}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="py-16 text-center text-muted-foreground">
                    <p className="font-semibold">No requests match current filter criteria</p>
                    <p className="text-xs mt-1">Total requests: 0</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SEND SUMMARY CARD MODAL / DRAWER                                */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {showSummaryCardModal && (
        <div className="fixed inset-0 top-14 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in">
          <div className="w-full max-w-2xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-5 py-4 bg-muted/20">
              <div className="flex items-center gap-2.5">
                <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-semibold">
                  <Send className="size-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground">Dispatch Support Summary Card</h3>
                  <p className="text-xs text-muted-foreground">
                    Send real-time performance summary via Google Chat webhook (cardsV2) or Email
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSummaryCardModal(false)}
                className="rounded-lg p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5 text-xs">
              {/* Settings Form */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-muted/20 p-4 rounded-xl border border-border">
                <div className="space-y-1">
                  <label className="font-semibold text-foreground block">Report Topic / Cadence *</label>
                  <select
                    value={summaryType}
                    onChange={(e) => setSummaryType(e.target.value as any)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground outline-none cursor-pointer focus:border-primary"
                  >
                    <option value="daily">Daily Summary (DAILY DIGEST)</option>
                    <option value="weekly">Weekly Summary (WEEKLY DIGEST)</option>
                    <option value="monthly">Monthly Summary (MONTHLY DIGEST)</option>
                    <option value="custom">Custom Filter Digest (CURRENT VIEW)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-foreground block">Destination Channel *</label>
                  <select
                    value={summaryChannel}
                    onChange={(e) => setSummaryChannel(e.target.value as any)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground outline-none cursor-pointer focus:border-primary"
                  >
                    <option value="email">Email Only (SMTP)</option>
                    <option value="gchat_webhook">Google Chat Webhook (cardsV2)</option>
                    <option value="both">Both Google Chat &amp; Email</option>
                  </select>
                </div>

                {/* Email Recipients */}
                {(summaryChannel === 'email' || summaryChannel === 'both') && (
                  <div className="space-y-1 sm:col-span-2">
                    <label className="font-semibold text-foreground block">Recipient Email(s) *</label>
                    <input
                      type="text"
                      value={summaryEmail}
                      onChange={(e) => setSummaryEmail(e.target.value)}
                      placeholder="admin@trustybytes.com, executive@trustybytes.com"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground outline-none focus:border-primary"
                    />
                    <p className="text-[11px] text-muted-foreground">Separate multiple recipients with commas</p>
                  </div>
                )}

                {/* GChat Webhook URL */}
                {(summaryChannel === 'gchat_webhook' || summaryChannel === 'both') && (
                  <div className="space-y-1 sm:col-span-2">
                    <label className="font-semibold text-foreground block">Google Chat Webhook URL *</label>
                    <input
                      type="url"
                      value={summaryWebhookUrl}
                      onChange={(e) => setSummaryWebhookUrl(e.target.value)}
                      placeholder="https://chat.googleapis.com/v1/spaces/.../messages?key=..."
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground font-mono outline-none focus:border-primary"
                    />
                    <p className="text-[11px] text-muted-foreground">Posts cardsV2 with KPI rows and report link</p>
                  </div>
                )}

                {/* Custom Title */}
                <div className="space-y-1 sm:col-span-2">
                  <label className="font-semibold text-foreground block">Custom Card Title</label>
                  <input
                    type="text"
                    value={summaryCustomTitle}
                    onChange={(e) => setSummaryCustomTitle(e.target.value)}
                    placeholder="Executive IT Support Digest"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground outline-none focus:border-primary"
                  />
                </div>

                {/* Schedule recurring */}
                <div className="sm:col-span-2 pt-2 border-t border-border flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">Save as Recurring Schedule</p>
                    <p className="text-[11px] text-muted-foreground">Automatically dispatch summary card via cron</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={scheduleEnabled}
                    onChange={(e) => setScheduleEnabled(e.target.checked)}
                    className="size-4 rounded border-border cursor-pointer text-primary"
                  />
                </div>

                {scheduleEnabled && (
                  <div className="space-y-1 sm:col-span-2">
                    <label className="font-semibold text-foreground block">Cron Expression *</label>
                    <select
                      value={scheduleCron}
                      onChange={(e) => setScheduleCron(e.target.value)}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono font-semibold text-foreground outline-none cursor-pointer focus:border-primary"
                    >
                      <option value="0 9 * * 1">Every Monday at 9:00 AM (0 9 * * 1)</option>
                      <option value="0 9 * * *">Every Day at 9:00 AM (0 9 * * *)</option>
                      <option value="0 9 1 * *">1st of Every Month at 9:00 AM (0 9 1 * *)</option>
                      <option value="0 18 * * 5">Every Friday at 6:00 PM (0 18 * * 5)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Recurring Schedules Manager */}
              {schedules.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider">Active Recurring Schedules</h4>
                  <div className="space-y-1.5">
                    {schedules.map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-muted/10 text-xs"
                      >
                        <div>
                          <p className="font-semibold text-foreground">{s.name}</p>
                          <p className="text-[11px] text-muted-foreground font-mono">
                            Channel: {s.channel} | Cron: {s.cron} {s.last_sent_at ? `| Last sent: ${new Date(s.last_sent_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => sendScheduleNow(s.id)}
                            title="Run Schedule Now"
                            className="p-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                          >
                            <Play className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteSchedule(s.id)}
                            title="Delete Schedule"
                            className="p-1 rounded text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950 transition-colors cursor-pointer"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2.5 border-t border-border px-5 py-3.5 bg-muted/20">
              <Button variant="secondary" size="sm" onClick={() => setShowSummaryCardModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={isDispatchingSummary}
                onClick={handleDispatchSummary}
                className="flex items-center gap-1.5"
              >
                <Send className="size-3.5" />
                <span>{isDispatchingSummary ? 'Sending...' : scheduleEnabled ? 'Send & Save Schedule' : 'Send Summary Card'}</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default SupportReports;
